import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { faCog, faTrash, faDownload, faUpload, faCopy, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import DataSource from 'devextreme/data/data_source';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject, takeUntil } from 'rxjs';
import { MachineInfo, MachineInfoService, MachineOwner } from 'src/api/generated';
import { MachineInfoMainService } from 'src/app/shared/services/machine-info.service';
import { MachineOwnerService } from 'src/app/shared/services/owner.service';
import { LoadingService } from 'src/app/shared/services/loading.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateWithFallbackPipe } from 'acl-cloud-lib';
import { RemoteDataWrapperService } from '../../../shared/services/remote-data/remote-data-wrapper.service';


@Component({
  selector: 'app-machine-info-config',
  templateUrl: './machine-info-config.component.html',
  styleUrl: './machine-info-config.component.scss'
})
export class MachineInfoConfigComponent implements OnInit, OnDestroy {
  faCog = faCog;
  faTrash = faTrash;
  faDownload = faDownload;
  faUpload = faUpload;
  faCopy = faCopy;
  faPenToSquare = faPenToSquare;
  modalRef?: BsModalRef;
  file: File | undefined;
  message: string = "";
  progress: number = 0;
  isSmallScreen: boolean = false;
  public loading: boolean = false;
  private _unsubscriber = new Subject();
  loadingPage: boolean = true;

  public actualMachineInfo: MachineInfo | undefined;
  public downloadLoading: boolean = false;

  //declares a FormGroup
  public frmData: FormGroup;
  public formData: FormGroup;
  public ownersDs: DataSource<MachineOwner, string>;

  constructor(
    public readonly loadingService: LoadingService,
    private _miSvc: MachineInfoMainService,
    private _oSvc: MachineOwnerService,
    private _router: Router,
    private _modalService: BsModalService,
    private breakpointObserver: BreakpointObserver,
    private readonly _machineInfoSvc: MachineInfoService,
    private readonly _notification: ToastrService,
    private readonly _translate: TranslateWithFallbackPipe,
    private readonly _remoteDataWrapperService: RemoteDataWrapperService,
    fb: FormBuilder
  ) {
    this.ownersDs = _oSvc.ListDataSource;
    //We define the FormGroup
    this.frmData = fb.group({
      OwnerName: new FormControl('')
    });

    this.formData = fb.group({
      MachineName: new FormControl('')
    });

    _miSvc.actualMachine$.pipe(takeUntil(this._unsubscriber)).subscribe(val => {
      this.actualMachineInfo = val;
    });
  }

  ngOnDestroy(): void {
    this._unsubscriber.next(null);
    this._unsubscriber.complete();
    this.MiSvc.AutoStatus = false;
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe([Breakpoints.HandsetPortrait])
      .subscribe((state: BreakpointState) => {
        this.isSmallScreen = state.matches;
      });
  }

  public get MiSvc(): MachineInfoMainService {
    return this._miSvc;
  }

  public getTranslateOwnerIdToName(a: string | undefined): string | undefined {
    if (a != undefined) {
      return this._oSvc.getOwnerNameById(a);
    } else {
      return undefined;
    }
  }

  openModal(template: TemplateRef<any>) {
    this.modalRef = this._modalService.show(template,);
  }

  public async toTrash(): Promise<void> {
    this.modalRef?.hide();
    this.loadingService.startLoading();

    const machineName = this.MiSvc.ActualMachine?.Name ?? "";
    const deletedMachineId = this.MiSvc.ActualMachine?.Id;

    this._machineInfoSvc.softDeleteAllDataMachineInfo(machineName).subscribe({
      next: async () => {
        // Deletion successful - now disconnect the machine
        try {
          await this._remoteDataWrapperService.disconnectMachine(machineName);
        } catch (error) {
          console.error('Error disconnecting machine after deletion:', error);
          // Machine deleted but disconnect failed - log it but continue
        }

        this._miSvc.dataSource.reload();

        // Check if the deleted machine was the current/actual one
        if (this._miSvc.CurrentMachine?.Id === deletedMachineId) {
          // Reload all machines and set the first one as current
          this._miSvc.getAllMachines().subscribe({
            next: (machines) => {
              this.loadingService.stopLoading();
              this._notification.success(this._translate.transform('machine-successfully-deleted'));
              this._router.navigate(['/machines']);
            },
            error: (err) => {
              this.loadingService.stopLoading();
              this._notification.error(this._translate.transform('failed-to-reload-machines'));
              this._router.navigate(['/machines']);
            }
          });
        } else {
          this.loadingService.stopLoading();
          this._notification.success(this._translate.transform('machine-successfully-deleted'));
          this._router.navigate(['/machines']);
        }
      },
      error: (err: HttpErrorResponse) => {
        this._notification.error(this._translate.transform('failed-to-delete-machine'));
        this.loadingService.stopLoading();
        // Machine NOT deleted - don't disconnect
      }
    });
  }

  download = () => {
    this.downloadLoading = true;
    this._miSvc.backUp().subscribe({
      next: event => {
        this._downloadFile(event);
        this.downloadLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this._notification.error(this._translate.transform('failed-to-backup-machine'));
        this.downloadLoading = false;
      }
    });
  }

  private _downloadFile(data: any) {
    const a = document.createElement('a');
    a.setAttribute('style', 'display:none;');
    document.body.appendChild(a);
    a.download = data.fileName;
    a.href = URL.createObjectURL(data.file);
    a.target = '_blank';
    a.click();
    document.body.removeChild(a);
  }

  handle(e: Event) { }

  decline(): void {
    this.modalRef?.hide();
  }

  public async changeOwn(): Promise<void> {
    this.modalRef?.hide();
    this.loadingService.startLoading();

    if (this.frmData.get('OwnerName') != null) {
      const newOwner = this.frmData.get('OwnerName')!.value;
      this.MiSvc.moveToOtherOwner(newOwner.Name).subscribe({
        next: () => {
          let currentMachine = this._miSvc.ActualMachine;
          if (currentMachine) {
            currentMachine.OwnerId = newOwner.Id;
          }
          this._miSvc.CurrentMachine = currentMachine;

          this.loadingService.stopLoading();
          this._notification.success(this._translate.transform('owner-successfully-changed'));

          // Navigate and then reload
          this._router.navigate(['/machines']).then(() => {
            this._miSvc.dataSource.reload();
          });
        },
        error: (err: HttpErrorResponse) => {
          this._notification.error(this._translate.transform('failed-to-change-owner'));
          this.loadingService.stopLoading();
        }
      });
    }
  }

  copy(): void {
    this.modalRef?.hide();
    this.loadingService.startLoading();
    if (this.formData.get('MachineName') != null) {
      this.MiSvc.copy(this.formData.get('MachineName')!.value).subscribe({
        next: () => {
          this._miSvc.dataSource.reload();
          this.loadingService.stopLoading();
          this._notification.success(this._translate.transform('machine-successfully-copied'));
          this._router.navigate(['/machines']);
        },
        error: (err: HttpErrorResponse) => {
          this._notification.error(this._translate.transform('failed-to-copy-machine'));
          this.loadingService.stopLoading();
        }
      });
    }
  }

  move(): void {
    this.modalRef?.hide();
    this.loadingService.startLoading();

    const movedMachineId = this.MiSvc.ActualMachine?.Id;
    const newMachineName = this.formData.get('MachineName')?.value;

    if (!newMachineName) {
      this.loadingService.stopLoading();
      return;
    }

    this.MiSvc.move(newMachineName).subscribe({
      next: () => {
        this._miSvc.dataSource.reload().then(() => {
          const wasActual = this._miSvc.ActualMachine?.Id === movedMachineId;
          const wasCurrent = this._miSvc.CurrentMachine?.Id === movedMachineId;

          // ✅ Only update if the moved machine was selected (Actual)
          if (wasActual) {
            this._miSvc.getOneMachine(movedMachineId!).subscribe({
              next: (updatedMachine) => {
                // Replace the selected machine with its new state
                this._miSvc.ActualMachine = updatedMachine;

                // If it was also the current, update that too
                if (wasCurrent) {
                  this._miSvc.CurrentMachine = updatedMachine;
                }

                this.loadingService.stopLoading();
                this._notification.success(this._translate.transform('machine-successfully-moved'));
                this._router.navigate(['/machines']);
              },
              error: (err) => {
                this.loadingService.stopLoading();
                this._notification.error(this._translate.transform('failed-to-load-moved-machine'));
                this._router.navigate(['/machines']);
              }
            });
          } else {
            // Moved machine wasn’t selected — just finish
            this.loadingService.stopLoading();
            this._notification.success(this._translate.transform('machine-successfully-moved'));
            this._router.navigate(['/machines']);
          }
        });
      },
      error: (err: HttpErrorResponse) => {
        this._notification.error(this._translate.transform('failed-to-move-machine'));
        this.loadingService.stopLoading();
      }
    });
  }

  setCurrentMachine(): void {
    this._miSvc.CurrentMachine = this._miSvc.ActualMachine;
    this._notification.success(this._translate.transform('machine-set-as-current'));
  }
}
